import React, { useState } from 'react';
import styled from 'styled-components';

import MultipleSelector from '@shared/components/inputs/selects/multiple-selector/default-multiple-selector';
import SingleValueWithDropDown from '@shared/components/inputs/selects/multiple-selector/components/SingleValueWithDropDown';
import MenuWithDraggableOption from '@shared/components/inputs/selects/multiple-selector/components/with-draggable/MenuWithDraggableOption';
import DropDownWithSwitch from '@shared/components/inputs/selects/multiple-selector/components/drop-down-with-switch';
import { addIndex } from '@domain/_screens/overview/campaign-table/utils';
import {
    checkColumn,
    formatAndCheckColumnsByCheckedMetric,
    getCheckedColumns,
    getColumnsValue,
    setColumnsIndexes,
    sortColumnsByIndex,
} from './column-table-menu.utils';

import {
    COLUMN_TABLE_TEXTS,
} from './column-table-menu.constants';
import { SelectorMetricColumn, SelectorMetricColumns } from './column-table-menu.types';

interface ColumnTableMenuProps {
    dataAppcuesId: string,
    selectedColumns: Array<string>,
    setSelectedColumns: Function,
    metrics: SelectorMetricColumns;
    advancedMetrics: SelectorMetricColumns;
}

export const ColumnsTableMenu = ({
    dataAppcuesId,
    selectedColumns,
    setSelectedColumns,
    metrics,
    advancedMetrics,
}: ColumnTableMenuProps) => {
    const StyledSingleValue = styled(SingleValueWithDropDown)`margin-right: 15px;`;

    const indexedMetrics = Object.values(addIndex(metrics, 0));

    const [isAdvancedMetricsActive, toggleAdvancedMetrics] = useState(false);
    const [columns, setColumns] = useState<Array<SelectorMetricColumn>>(
        formatAndCheckColumnsByCheckedMetric(indexedMetrics, selectedColumns),
    );
    const handleChangeColumn = (newColumns: Array<SelectorMetricColumn>,
        selectedColumn?: SelectorMetricColumn) => {
        setColumns(newColumns);
        const checkedColumns = getCheckedColumns(newColumns);
        const checkedColumnsValues = getColumnsValue(checkedColumns);
        const uncheckedColumnsValues = selectedColumns.filter(
            (previouslySelectedColumn) => {
                const isPreviouslySelectedColumnSelectedAgain = previouslySelectedColumn
                === selectedColumn?.value;
                return isPreviouslySelectedColumnSelectedAgain
                    ? null : !checkedColumnsValues.includes(previouslySelectedColumn);
            },
        );

        setSelectedColumns([...checkedColumnsValues, ...uncheckedColumnsValues]);
    };

    const onClickOption = (selectedColumn: SelectorMetricColumn) => {
        const newColumns = checkColumn(columns, selectedColumn);

        handleChangeColumn(newColumns, selectedColumn);
    };

    const onDragEnd = ({ destination, source }) => {
        if (!source || !destination) return;
        const newColumns = [...columns];
        const [draggedColumn] = newColumns.splice(source.index, 1);

        newColumns.splice(destination.index, 0, draggedColumn);
        handleChangeColumn(setColumnsIndexes(newColumns));
    };

    const filterAdvancedColumns = () => columns.map((column) => {
        const isAdvancedMetric = column.value in advancedMetrics;
        return ({
            ...column,
            isVisible: isAdvancedMetric ? isAdvancedMetricsActive : true,
        });
    });

    const options = sortColumnsByIndex(filterAdvancedColumns());

    const selectedOptions = getCheckedColumns(columns);

    return (
        <MultipleSelector
            dataAppcuesId={dataAppcuesId}
            onChange={onClickOption}
            options={options}
            title={COLUMN_TABLE_TEXTS.SELECT_TITLE}
            singleValue={{
                component: StyledSingleValue,
                props: {
                    displayValue: false,
                    values: selectedOptions,
                    placeholder: COLUMN_TABLE_TEXTS.METRICS_PLACEHOLDER,
                },
            }}
            components={{
                SubMenu: MenuWithDraggableOption,
                props: { onDragEnd },
            }}
            dropdownMenu={{
                component: DropDownWithSwitch,
                props: {
                    onToggleSwitch: () => toggleAdvancedMetrics(!isAdvancedMetricsActive),
                    isSwitchActive: isAdvancedMetricsActive,
                    switchLabel: COLUMN_TABLE_TEXTS.SELECT_LABEL_SWITCH,
                },
            }}
        />
    );
};
